import { useState, useEffect } from 'react';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import PrintIcon from '@mui/icons-material/Print';
import dayjs from 'dayjs';
import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import { Box, Typography } from '@mui/material';

import { clearEditMode, dataGridPremiumColumns } from '../../../utils/helper';
import {
  DataGridIdentifiers,
  FormErrorUI,
  InputUI,
  AddButtonUi,
  AutoCompleteSelectUI,
  DatagridPremiumUi,
  CheckboxUI,
  DateTimePickerUI,
  OrderArrowsUI,
} from '../../Interface';
import { NotificationType } from '../../../config';
import addNotification from '../../../utils/addNotification';
import { formatNumber } from '../../../utils/currencyHelper';
import { DependenceTypeList } from '../../../config/config';
import TaskFeedbackDialog from '../../TaskFeedback/TaskFeedbackDialog';
import useUsers from '../../../context/UsersContext';

const EditToolbar = (props) => {
  const { disabled, push, defaultField } = props;
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    const maxVisOrder = Math.max(...(values.productionOperation?.map((item) => item.visOrder) ?? []), 0);
    const newVisOrder = maxVisOrder + 1;

    defaultField.id = uuidv4();
    defaultField.visOrder = newVisOrder;
    await push(defaultField);

    const fieldsToValidate = [
      `productionOperation.${values.productionOperation.length ?? 0}.operationId`,
      `productionOperation.${values.productionOperation.length ?? 0}.resourceOneId`,
      `productionOperation.${values.productionOperation.length ?? 0}.productionStatusId`,
      `productionOperation.${values.productionOperation.length ?? 0}.startDate`,
      `productionOperation.${values.productionOperation.length ?? 0}.dependence`,
      `productionOperation.${values.productionOperation.length ?? 0}.description`,
      `productionOperation.${values.productionOperation.length ?? 0}.quantityUnitId`,
      `productionOperation.${values.productionOperation.length ?? 0}.baseQty`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
};

const ProductionOperations = ({
  disabled,
  defaultField,
  autocompleteOptions,
  sourceData,
  calcOperationBaseRate,
  autocompleteData,
  params,
  selectedValues,
}) => {
  const { user } = useUsers();
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  const [lastChangedIndex, setLastChangedIndex] = useState(null);

  const defFeedbackData = {
    open: false,
    data: {},
  };
  const [feedbackData, setFeedbackData] = useState(defFeedbackData);

  const initialState = {
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [{ field: 'visOrder', sort: 'asc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 25,
        page: 0,
      },
    },
    density: 'compact',
  };

  useEffect(() => {
    if (lastChangedIndex !== null) {
      calcOperationBaseRate(lastChangedIndex);
    }
  }, [values?.productionOperation?.[lastChangedIndex]?.baseQty, lastChangedIndex]);

  const getValueIndex = (values, id) => {
    return values.productionOperation.findIndex((rc) => rc.id === id);
  };

  const handleOrderChange = (currentOrder, direction) => {
    const index = values.productionOperation.findIndex((item) => item.visOrder === currentOrder);
    const swapIndex = direction === 'up' ? index - 1 : index + 1;

    if (swapIndex >= 0 && swapIndex < values.productionOperation.length) {
      const updatedList = [...values.productionOperation];
      [updatedList[index], updatedList[swapIndex]] = [updatedList[swapIndex], updatedList[index]];

      updatedList[index].visOrder = currentOrder;
      updatedList[swapIndex].visOrder = currentOrder + (direction === 'up' ? -1 : 1);

      setFieldValue('productionOperation', updatedList);
    }
  };

  const handleDelete = (index) => {
    const findedOperation = values.productionOperation[index];
    clearEditMode(apiRef);
    if (findedOperation) {
      const updatedList = values.productionOperation.map((item) => {
        if (item.visOrder > findedOperation.visOrder) {
          return { ...item, visOrder: item.visOrder - 1 };
        }
        return item;
      });
      setFieldValue('productionOperation', updatedList);
    }
  };

  const columns = (setFieldValue, validateField, errors, remove) => {
    return dataGridPremiumColumns([
      {
        field: 'visOrder',
        type: 'string',
        headerName: '#',
        renderCell: (params) => {
          const handleUpClick = (e) => {
            clearEditMode(apiRef);
            e.stopPropagation();
            e.preventDefault();
            handleOrderChange(params.row.visOrder, 'up');
          };
          const handleDownClick = (e) => {
            clearEditMode(apiRef);
            e.stopPropagation();
            e.preventDefault();
            handleOrderChange(params.row.visOrder, 'down');
          };
          return (
            <Box display="flex" alignItems="center">
              <Box mr={1}>{params.row.visOrder}</Box>
              <OrderArrowsUI onUpClick={handleUpClick} onDownClick={handleDownClick} disabled={disabled} />
            </Box>
          );
        },
      },
      {
        field: 'label',
        headerName: 'Művelet azon.',
        type: 'string',
        sortable: false,
      },
      {
        field: 'operationId',
        headerName: 'Művelet',
        type: 'string',
        editable: true,
        sortable: false,
        renderCell: (data) => {
          const findedOperation = autocompleteOptions?.operation?.find((item) => item.value === data.row.operationId);
          return findedOperation?.label;
        },

        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedOperation = autocompleteOptions?.operation?.find((item) => item.value === data.row.operationId);

          const dataset = autocompleteOptions?.operation;

          return (
            <Field
              name={`productionOperation.${valueIndex}.operationId`}
              variant="standard"
              component={AutoCompleteSelectUI}
              fullWidth
              dataset={dataset}
              selectedValue={values.productionOperation?.[valueIndex]?.operationId}
              selectedLabelValue={findedOperation?.label}
              helperText={<FormErrorUI message={errors?.productionOperation?.[valueIndex]?.operationId} />}
              onChange={(_e, newVal) => {
                setFieldValue(`productionOperation.${valueIndex}.operationId`, newVal?.value ?? null);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newVal?.value ?? null,
                });
                const selectedOperation = autocompleteData.operation.find((i) => i.operationId === newVal?.value);
                if (selectedOperation) {
                  setFieldValue(
                    `productionOperation.${valueIndex}.resourceOneId`,
                    selectedOperation?.resourceOneId ?? null
                  );
                  data.api.setEditCellValue({
                    id: data.id,
                    field: 'resourceOneId',
                    value: selectedOperation?.resourceOneId ?? null,
                  });
                }
              }}
              onBlur={async () => await validateField(`productionOperation.${valueIndex}.operationId`)}
            />
          );
        },
      },
      {
        field: 'resourceOneId',
        headerName: 'Erőforrás',
        type: 'string',
        editable: true,
        disableOpenOnFocus: true,
        sortable: false,
        renderCell: (data) => {
          const findedReesourceOne = autocompleteOptions.resourceOne.find(
            (item) => item.value === data.row.resourceOneId
          );
          return findedReesourceOne?.label;
        },

        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedReesourceOne = autocompleteOptions.resourceOne.find(
            (item) => item.value === data.row.resourceOneId
          );

          const operationLine = values.productionOperation?.[valueIndex];

          const findedOperation = sourceData?.sourceListOperation?.find(
            (so) => so.operationId === operationLine.operationId
          );

          const dataset = autocompleteOptions.resourceOne;

          return (
            <Field
              name={`productionOperation.${valueIndex}.resourceOneId`}
              variant="standard"
              component={AutoCompleteSelectUI}
              fullWidth
              dataset={dataset}
              selectedValue={values.productionOperation?.[valueIndex]?.resourceOneId}
              selectedLabelValue={findedReesourceOne?.label}
              helperText={<FormErrorUI message={errors?.productionOperation?.[valueIndex]?.resourceOneId} />}
              onChange={async (_e, newVal) => {
                await setFieldValue(`productionOperation.${valueIndex}.resourceOneId`, newVal?.value ?? null);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newVal?.value ?? null,
                });
              }}
              onBlur={async () => await validateField(`productionOperation.${valueIndex}.resourceOneId`)}
            />
          );
        },
      },
      {
        field: 'productionStatusId',
        headerName: 'Gyártás státusz',
        type: 'string',
        editable: true,
        sortable: false,
        renderCell: (data) => {
          const findedProdStatus = autocompleteOptions.prodStatus.find(
            (item) => item.value === data.row.productionStatusId
          );
          return findedProdStatus?.label;
        },

        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedProdStatus = autocompleteOptions.prodStatus.find(
            (item) => item.value === data.row.productionStatusId
          );

          return (
            <Field
              name={`productionOperation.${valueIndex}.productionStatusId`}
              variant="standard"
              component={AutoCompleteSelectUI}
              fullWidth
              dataset={autocompleteOptions.prodStatus}
              value={findedProdStatus}
              helperText={<FormErrorUI message={errors?.productionOperation?.[valueIndex]?.productionStatusId} />}
              onChange={(_e, newVal) => {
                setFieldValue(`productionOperation.${valueIndex}.productionStatusId`, newVal?.value ?? null);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newVal?.value ?? null,
                });
              }}
              onBlur={async () => await validateField(`productionOperation.${valueIndex}.productionStatusId`)}
            />
          );
        },
      },
      {
        field: 'startDate',
        headerName: 'Gy. terv. kezdete',
        type: 'date',
        editable: true,
        sortable: false,
        type: 'dateTime',
        valueGetter: (_data, row) => {
          return new Date(row.startDate);
        },
        renderCell: (data) => {
          return dayjs(data.row.startDate).format('YYYY.MM.DD. HH:mm');
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return (
            <Field
              name={`productionOperation.${valueIndex}.startDate`}
              component={DateTimePickerUI}
              helperText={<FormErrorUI message={errors?.productionOperation?.[valueIndex]?.startDate} />}
              variant="standard"
              value={dayjs(data.row?.startDate)}
              onChange={(newDate) => {
                const dateObject = newDate.toDate();
                setFieldValue(`productionOperation.${valueIndex}.startDate`, dateObject);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: dateObject,
                });
              }}
              onBlur={async () => await validateField(`productionOperation.${valueIndex}.startDate`)}
            />
          );
        },
      },
      {
        field: 'dependence',
        headerName: 'Függőség',
        type: 'string',
        editable: true,
        sortable: false,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedDependence = DependenceTypeList.find(
            (u) => u.value === values.productionOperation?.[valueIndex]?.dependence
          );

          return findedDependence?.label;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedDependence = DependenceTypeList.find(
            (u) => u.value === values.productionOperation?.[valueIndex]?.dependence
          );
          return (
            <Field
              name={`productionOperation.${valueIndex}.dependence`}
              type="text"
              variant="standard"
              as={AutoCompleteSelectUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.productionOperation?.[valueIndex]?.dependence} />}
              onChange={(_e, newVal) => {
                setFieldValue(`productionOperation.${valueIndex}.dependence`, newVal?.value ?? null);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newVal?.value ?? null,
                });
              }}
              onBlur={async () => await validateField(`productionOperation.${valueIndex}.dependence`)}
              value={findedDependence}
              isOptionEqualToValue={(option, value) => option.value === value}
              dataset={DependenceTypeList}
            />
          );
        },
      },
      {
        field: 'baseRate',
        headerName: 'Alaphányad',
        type: 'string',
        sortable: false,
        renderCell: (data) => formatNumber(data.row.baseRate),
      },
      {
        field: 'plannedQty',
        headerName: 'Mennyiség',
        type: 'string',
        sortable: false,
        /*valueGetter: (data, row) => {
          const valueIndex = getValueIndex(values, row.id);
          return values?.productionOperation?.[valueIndex]?.plannedQty;
        },*/
      },
      {
        field: 'quantityUnitId',
        headerName: 'Mennyiségi egység',
        type: 'string',
        editable: true,
        sortable: false,
        renderCell: (data) => {
          const findedQtyUnit = autocompleteOptions.quantityUnit.find((item) => item.value === data.row.quantityUnitId);
          return findedQtyUnit?.label;
        },

        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedQtyUnit = autocompleteOptions.quantityUnit.find((item) => item.value === data.row.quantityUnitId);

          return (
            <Field
              disabled
              name={`productionOperation.${valueIndex}.quantityUnitId`}
              variant="standard"
              component={AutoCompleteSelectUI}
              fullWidth
              dataset={autocompleteOptions.quantityUnit}
              value={findedQtyUnit}
              helperText={<FormErrorUI message={errors?.productionOperation?.[valueIndex]?.quantityUnitId} />}
              onChange={(_e, newVal) => {
                setFieldValue(`productionOperation.${valueIndex}.quantityUnitId`, newVal?.value ?? null);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newVal?.value ?? null,
                });
              }}
              onBlur={async () => await validateField(`productionOperation.${valueIndex}.quantityUnitId`)}
            />
          );
        },
      },
      {
        field: 'realTimeSpent',
        headerName: 'Tény ráfordítás',
        type: 'string',
        editable: false,
        sortable: false,
        valueGetter: (data, row) => {
          const valueIndex = getValueIndex(values, row.id);

          const operationData = values?.productionOperation?.[valueIndex];

          const foundedQuantityUnit = autocompleteData.quantityUnit.find(
            (unit) => unit.id === values.productionOperation?.[valueIndex]?.quantityUnitId
          );

          if (operationData?.productionReport) {
            const sum = operationData.productionReport.reduce((acc, current) => {
              const valueToAdd =
                foundedQuantityUnit?.code === params.QUANTITY_UNIT_HOUR_CODE
                  ? parseFloat(current.timeSpent)
                  : current.timeSpentInMin;

              return acc + valueToAdd;
            }, 0);

            return Math.round(sum * 100) / 100;
          }

          return 0;
        },
      },
      {
        field: 'realQuantityUnitId',
        headerName: 'Mennyiségi egység',
        type: 'string',
        editable: true,
        sortable: false,
        renderCell: (data) => {
          const findedQtyUnit = autocompleteOptions.quantityUnit.find((item) => item.value === data.row.quantityUnitId);
          return findedQtyUnit?.label;
        },

        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedQtyUnit = autocompleteOptions.quantityUnit.find((item) => item.value === data.row.quantityUnitId);

          return (
            <Field
              disabled
              name={`productionOperation.${valueIndex}.quantityUnitId`}
              variant="standard"
              component={AutoCompleteSelectUI}
              fullWidth
              dataset={autocompleteOptions.quantityUnit}
              value={findedQtyUnit}
              helperText={<FormErrorUI message={errors?.productionOperation?.[valueIndex]?.quantityUnitId} />}
              onChange={(_e, newVal) => {
                setFieldValue(`productionOperation.${valueIndex}.quantityUnitId`, newVal?.value ?? null);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newVal?.value ?? null,
                });
              }}
              onBlur={async () => await validateField(`productionOperation.${valueIndex}.quantityUnitId`)}
            />
          );
        },
      },

      {
        field: 'description',
        headerName: 'Megjegyzés',
        type: 'string',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`productionOperation.${valueIndex}.description`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.productionOperation?.[valueIndex]?.description} />}
              onChange={(e) => {
                setFieldValue(`productionOperation.${valueIndex}.description`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`productionOperation.${valueIndex}.description`)}
            />
          );
        },
      },
      {
        field: 'chk',
        type: 'actions',
        cellClassName: 'actions',
        headerName: 'Lejelentési pont',
        sortable: false,
        getActions: ({ id, row }) => {
          return [
            <Field
              name="a"
              type="checkbox"
              as={CheckboxUI}
              fullWidth
              //label={<FormLabelUI text="Lejelentési pont" />}
              disabled={disabled}
            />,
          ];
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id, row }) => {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                const valueIndex = getValueIndex(values, id);
                handleDelete(valueIndex);
                return remove(valueIndex);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
      {
        field: 'print',
        type: 'actions',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id, row }) => {
          return [
            <GridActionsCellItem
              icon={<PrintIcon />}
              label="Save"
              onClick={() =>
                addNotification({
                  content: 'Nyomtatás elindult!',
                  type: NotificationType.INFO,
                })
              }
              color="inherit"
            />,
          ];
        },
      },
      {
        field: 'fixedLeadTime',
        headerName: 'Fix átfutási idő',
        type: 'string',
        sortable: false,
      },
      {
        field: 'baseQty',
        headerName: 'Terv. mennyiség',
        type: 'string',
        editable: true,
        sortable: false,
        valueGetter: (data, row) => {
          const valueIndex = getValueIndex(values, row.id);
          return values?.productionOperation?.[valueIndex]?.baseQty;
        },

        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`productionOperation.${valueIndex}.baseQty`}
              type="number"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.productionOperation?.[valueIndex]?.baseQty} />}
              onChange={(e) => {
                setFieldValue(`productionOperation.${valueIndex}.baseQty`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
                setLastChangedIndex(valueIndex);
              }}
              onBlur={async () => await validateField(`productionOperation.${valueIndex}.baseQty`)}
            />
          );
        },
      },
      {
        field: 'feedback',
        type: 'actions',
        cellClassName: 'actions',
        headerName: 'Feedback',
        sortable: false,
        getActions: ({ row }) => {
          return [
            <GridActionsCellItem
              icon={
                <Box backgroundColor="rgba(240, 237, 45, 0.74)" p="0.4rem 0.6rem 0rem 0.6rem" borderRadius="12px">
                  <Typography fontWeight="bold">Fb</Typography>
                </Box>
              }
              label="Feedback"
              disabled={row.isNew || disabled}
              onClick={(e) => {
                e.stopPropagation();
                setFeedbackData({
                  open: true,
                  data: {
                    productionId: selectedValues.id,
                    prodOperationId: row.id,
                    prodOperationLabel: row.label,
                    createdBy: user.userId,
                    itemCode: selectedValues.articleNumber,
                    baseLine: selectedValues.baseLine,
                  },
                });
              }}
              color="inherit"
            />,
          ];
        },
      },
    ]);
  };

  return (
    <>
      <div className="overflow-x-auto">
        <div className="align-middle">
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
            <FieldArray name="productionOperation">
              {({ remove, push }) => (
                <DatagridPremiumUi
                  apiRef={apiRef}
                  datagridIdentifier={DataGridIdentifiers.ProductionFormTable}
                  height={480}
                  datagridInitialState={initialState}
                  columns={columns(setFieldValue, validateField, errors, remove)}
                  rows={values?.productionOperation ?? []}
                  editMode="row"
                  slots={{
                    toolbar: EditToolbar,
                  }}
                  slotProps={{
                    toolbar: {
                      disabled,
                      push,
                      defaultField,
                    },
                  }}
                  onCellDoubleClick={(_params, event) => {
                    event.defaultMuiPrevented = true;
                  }}
                  headerFilters={false}
                  getRowHeight={() => 'auto'}
                  onCellClick={(params, event) => {
                    if (disabled) return;

                    const rowsInEditMode = Object.keys(apiRef.current.state.editRows);

                    for (const rowId of rowsInEditMode) {
                      if (params.row.id.toString() !== rowId) {
                        apiRef.current.stopRowEditMode({ id: rowId });
                      }
                    }

                    const rowMode = apiRef.current.getRowMode(params.row.id);
                    if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                  }}
                />
              )}
            </FieldArray>
          </div>
        </div>
      </div>
      {feedbackData.open && (
        <TaskFeedbackDialog
          open={feedbackData.open}
          handleClose={() => setFeedbackData(defFeedbackData)}
          data={feedbackData.data}
        />
      )}
    </>
  );
};

export default ProductionOperations;
